<template>
  <div>
    <vs-row vs-justify="flex-end">
      <div class="vx-row mr-2 flex-1">
        <div class="vx-col mb-3">
          <h4>{{ $t(`places.categories.${request.category}`) }}</h4>
          <vs-row class="mt-3">{{ $t(`places.subjects.${request.subject}`) }}</vs-row>
        </div>
        <div class="vx-col mb-3">
          <vs-row class="font-semibold">{{ $t('entity.created_at') }}:</vs-row>
          <vs-row class="mt-3">{{ invoice.created_at | datetime }}</vs-row>
        </div>
        <div class="vx-col mb-3" v-if="request.date">
          <vs-row class="font-semibold">{{ $t('request_forms.request_datetime') }}:</vs-row>
          <vs-row class="mt-3" v-if="request.date">{{ request.date | datetime }}</vs-row>
          <vs-row class="mt-3" v-else>{{ $t('request_forms.for_now') }}</vs-row>
        </div>
        <div class="vx-col mb-3">
          <vs-row class="font-semibold">{{ $t('services.service_status') }}:</vs-row>
          <vs-row class="mt-3"> <cell-badges :data="request.status" local="services.status."/></vs-row>
        </div>
        <div class="vx-col mb-3">
          <vs-row class="font-semibold">{{ $t('invoices.status') }}:</vs-row>
          <vs-row class="mt-3"><cell-badges :data="invoice.state" local="invoices.states." /> </vs-row>
        </div>
      </div>
      <vs-row vs-justify="flex-end" class="vx-col w-full md:w-1/6 sm:w-1/4 mb-3">
        <vs-button color="primary" class="print-hidden" type="border" @click="printPage">
          {{ $t('invoices.print') }}
        </vs-button>
      </vs-row>
    </vs-row>
    <vs-row class="mb-base" v-if="isEmployeeAccepted">
      <vx-card no-shadow card-border :subtitle="$t('invoices.provider')">
        <vs-row class="mb-2">
          <div class="vx-col w-full md:w-1/4 sm:w-1/2 mb-3">
            <h4>
              {{ $t(`users.genders.name.${requestDetails.employeeUser.details.gender}`) }}
              {{ requestDetails.employeeUser.details.firstName }}
              {{ requestDetails.employeeUser.details.lastName }}
            </h4>
          </div>
          <div class="vx-col w-full md:w-1/4 sm:w-1/2 mb-3">
            <vs-row class="font-semibold">{{ $t('invoices.employer') }}:</vs-row>
            <vs-row class="mt-3">{{ requestDetails.place.alias | persian_digits }}</vs-row>
          </div>
          <div class="vx-col w-full md:w-1/4 sm:w-1/2 mb-3">
            <vs-row class="font-semibold">{{ $t('users.phone') }}:</vs-row>
            <vs-row class="mt-3">{{ requestDetails.employeeUser.phone | persian_digits }}</vs-row>
          </div>
        </vs-row>
        <vs-row class="mb-2" v-if="request.commendation">
          <div class="vx-col w-full font-semibold">{{ $t('invoice.commendation') }}:</div>
          <div class="vx-col w-full mt-3">{{ request.commendation }}</div>
        </vs-row>
      </vx-card>
    </vs-row>
    <vs-row class="mb-base" v-if="requestDetails.caseManagerUser">
      <vx-card no-shadow card-border :subtitle="$t('case_manager.case_manager')">
        <vs-row class="mb-2">
          <div class="vx-col w-full md:w-1/4 sm:w-1/2 mb-3">
            <h4>
              {{ $t(`users.genders.name.${requestDetails.caseManagerUser.details.gender}`) }}
              {{ requestDetails.caseManagerUser.details.firstName }}
              {{ requestDetails.caseManagerUser.details.lastName }}
            </h4>
          </div>
          <div class="vx-col w-full md:w-1/4 sm:w-1/2 mb-3">
            <vs-row class="font-semibold">{{ $t('users.phone') }}:</vs-row>
            <vs-row class="mt-3">{{ requestDetails.caseManagerUser.phone | persian_digits }}</vs-row>
          </div>
        </vs-row>
      </vx-card>
    </vs-row>
    <vs-row class="mb-base">
      <vx-card no-shadow card-border :subtitle="$t('invoices.receiver')">
        <vs-row class="mb-2">
          <div class="vx-col w-full md:w-1/5 sm:w-1/2 mb-3">
            <h4>
              {{ $t(`users.genders.name.${request.gender}`) }} {{ request.firstName }}
              {{ request.lastName }}
            </h4>
          </div>
          <div class="vx-col w-full md:w-1/5 sm:w-1/2 mb-3">
            <vs-row class="font-semibold">{{ $t('users.phone') }}:</vs-row>
            <vs-row class="mt-3">{{ request.phoneNumber | persian_digits }}</vs-row>
          </div>
          <div class="vx-col w-full md:w-1/5 sm:w-1/2 mb-3">
            <vs-row class="font-semibold">{{ $t('users.emergency_phone') }}:</vs-row>
            <vs-row class="mt-3">{{ request.emergencyNumber | persian_digits }}</vs-row>
          </div>
          <div class="vx-col w-full md:w-1/5 sm:w-1/2 mb-3">
            <vs-row class="font-semibold">{{ $t('request_forms.age') }}:</vs-row>
            <vs-row class="mt-3">{{ request.age | persian_digits }}</vs-row>
          </div>
        </vs-row>
        <vs-row class="mb-3">
          <div class="vx-col w-full"><address-string :address="request.address" /></div>
        </vs-row>
        <vs-row class="mb-3">
          <open-street-map
            class="responsive card-map-top"
            view-only
            :markers="[{ latlng: [request.address.location.lat, request.address.location.lon] }]"
          />
        </vs-row>
      </vx-card>
    </vs-row>
    <vs-row class="mb-base" v-if="requestDetails.reserve">
      <vx-card no-shadow card-border :subtitle="$t('reservation.reserved')">
        <vs-row class="mb-2">
          <div class="vx-col w-full md:w-1/3 sm:w-1/2 mb-3">
            <vs-row class="font-semibold">{{ $t('services.start_time') }}:</vs-row>
            <vs-row class="mt-3">{{ requestDetails.reserve.startDate | datetime }}</vs-row>
          </div>
          <div class="vx-col w-full md:w-1/3 sm:w-1/2 mb-3">
            <vs-row class="font-semibold">{{ $t('services.end_time') }}:</vs-row>
            <vs-row class="mt-3">{{ requestDetails.reserve.endDate | datetime }}</vs-row>
          </div>
        </vs-row>
      </vx-card>
    </vs-row>
    <vs-row class="mb-base" v-if="request.description">
      <div class="vx-col w-full">{{ $t('documents.description') }}</div>
      <div class="vx-col w-full mt-3">{{ request.description }}</div>
    </vs-row>
    <download-files class="mb-base" :files="request.attachments" />
    <vs-table hoverFlat :data="invoice.items" class="mb-3">
      <template slot="thead">
        <vs-th>{{ $t('request_forms.service_name') }}</vs-th>
        <vs-th>{{ $t('request_forms.price') | thousands }}</vs-th>
        <vs-th>{{ $t('request_forms.number_of_service') }}</vs-th>
        <vs-th>{{ $t('request_forms.total_price') }}</vs-th>
      </template>
      <template slot-scope="{ data }">
        <tbody v-if="data.length">
          <vs-tr :data="tr" :key="indextr" v-for="(tr, indextr) in data">
            <vs-td>
              {{ tr.service_name }}
            </vs-td>
            <vs-td>{{ tr.price | thousands }}</vs-td>
            <vs-td>
              {{ tr.count | persian_digits }} {{ tr.service !== 'transport' ? $t(`units.${tr.unit}`) : '' }}
            </vs-td>
            <vs-td>
              {{ (tr.price * tr.count) | thousands }}
            </vs-td>
          </vs-tr>
        </tbody>
        <tfoot>
          <vs-tr>
            <vs-td></vs-td>
            <vs-td></vs-td>
            <vs-td class="text-right">{{ $t('invoices.wage') }}:</vs-td>
            <vs-td>{{ invoice.wage | thousands }}</vs-td>
          </vs-tr>
          <vs-tr class="font-semibold">
            <vs-td></vs-td>
            <vs-td></vs-td>
            <vs-td class="text-right">{{ $t('invoices.total') }}:</vs-td>
            <vs-td>{{ invoice.totalPrice | toman }}</vs-td>
          </vs-tr>
        </tfoot>
      </template>
    </vs-table>
  </div>
</template>

<script>
import CellBadges from '@/views/components/common/table-cells/CellBadges'
import OpenStreetMap from '@/views/components/maps/OpenStreetMap'
import AddressString from '@/views/components/maps/AddressString'
import DownloadFiles from '@/views/components/forms/DownloadFiles'

export default {
  name: 'InvoiceDataView',
  components: { DownloadFiles, AddressString, OpenStreetMap, CellBadges },
  props: {
    invoice: { type: Object, required: true },
    request: { type: Object, required: true },
    requestDetails: { type: Object, required: true }
  },
  computed: {
    stateOptions() {
      return this.$store.getters['enum/selectOptions']('InvoiceState', 'invoices.states')
    },
    isEmployeeAccepted() {
      return this.request.services.findIndex(i => ['accepted', 'finished'].includes(i.status)) >= 0
    }
  },
  methods: {
    printPage() {
      window.print()
    }
  }
}
</script>

<style lang="scss">
@media print {
  #box-widget-icon {
    // goftino
    visibility: hidden;
    * {
      visibility: hidden;
    }
  }
  .invoice-page {
    * {
      visibility: hidden;
    }

    #content-area {
      margin: 0 !important;
    }

    .vs-con-table {
      .vs-con-tbody {
        overflow: hidden !important;
      }
    }

    #invoice-container {
      position: absolute;
      right: 0;
      top: 0;
      box-shadow: none;
      margin: 0 !important;

      .print-hidden {
        visibility: hidden;
        * {
          visibility: hidden;
        }
      }

      * {
        visibility: visible;
      }
    }
  }
}

@page {
  size: 1100px;
}
</style>
